import React, { useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { ReduxRootStateType } from '../redux/store';
import { useGetFormsByCompany, useUploadForm, useDeleteForm } from '../api/reactQueryHook/useForms';
import { useGetConnectedFormsByCompany } from '../api/reactQueryHook/useConnectedForms';
import {
  Box, Typography, Divider, IconButton, CircularProgress,
  Dialog, DialogContent, Button, InputBase,
} from "@mui/material";
import DeleteIcon     from "@mui/icons-material/Delete";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import CloseIcon      from "@mui/icons-material/Close";
import SearchIcon     from "@mui/icons-material/Search";
import useNotification from '../hooks/useNotification';
import LoadingOverlay from './LoadingOverlay';

const MagicOverlay: React.FC<{ show: boolean }> = ({ show }) => <LoadingOverlay isLoading={show} />;

interface FormsViewProps {
  selectedCompanyId: number;
}

const FormsView: React.FC<FormsViewProps> = ({ selectedCompanyId }) => {
  const userInfo     = useSelector((state: ReduxRootStateType) => state.user.user_info);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { setMessage, setSeverity, handleSnackbarOpen } = useNotification();

  const { data }           = useGetFormsByCompany(selectedCompanyId);
  const { data: connData } = useGetConnectedFormsByCompany(selectedCompanyId);
  const uploadMutation     = useUploadForm(selectedCompanyId);
  const deleteMutation     = useDeleteForm(selectedCompanyId);

  const forms = [...(data?.data ?? [])].sort((a, b) => {
    const nameA = (a.s3_path.split('/').pop() ?? '').toLowerCase();
    const nameB = (b.s3_path.split('/').pop() ?? '').toLowerCase();
    return nameA.localeCompare(nameB);
  });

  const [search, setSearch] = useState('');
  const filteredForms = forms.filter(f =>
    (f.s3_path.split('/').pop() ?? '').toLowerCase().includes(search.toLowerCase())
  );

  const [confirmId,  setConfirmId]  = useState<number | null>(null);
  const connectedCount = confirmId !== null
    ? (connData?.data ?? []).filter(c => c.form_id === confirmId).length
    : 0;
  // Total submissions for the form = sum of per-file submission_count across its connected rows.
  const submissionCount = confirmId !== null
    ? (connData?.data ?? [])
        .filter(c => c.form_id === confirmId)
        .reduce((sum, c) => sum + (c.submission_count ?? 0), 0)
    : 0;
  const [isDragging, setIsDragging] = useState(false);

  const handleUploadClick = () => fileInputRef.current?.click();

  const handleConfirmDelete = () => {
    if (confirmId !== null) deleteMutation.mutate(confirmId);
    setConfirmId(null);
  };

  const MAX_FILE_SIZE = 25 * 1024 * 1024; // 25 MB

  const uploadFiles = (files: File[]) => {
    const valid = files.filter(f => /\.(pdf|doc|docx)$/i.test(f.name));
    if (!valid.length) return;
    valid.forEach(file => {
      const alreadyExists = forms.some(f => (f.s3_path.split('/').pop() ?? '') === file.name);
      if (alreadyExists) {
        setMessage(`"${file.name}" already exists. Delete the existing form first or rename the file.`);
        setSeverity('error');
        handleSnackbarOpen();
        return;
      }
      if (file.size > MAX_FILE_SIZE) {
        setMessage(`"${file.name}" exceeds the 25 MB limit and was not uploaded.`);
        setSeverity('error');
        handleSnackbarOpen();
        return;
      }
      const fd = new FormData();
      fd.append('file', file);
      fd.append('company_id', String(selectedCompanyId));
      fd.append('uploaded_by', String(userInfo?.user_id));
      fd.append('form_name', file.name.replace(/\.[^/.]+$/, ""));
      uploadMutation.mutate(fd);
    });
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    uploadFiles(Array.from(e.target.files ?? []));
    e.target.value = "";
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    uploadFiles(Array.from(e.dataTransfer.files));
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!e.currentTarget.contains(e.relatedTarget as Node)) setIsDragging(false);
  };


  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}>
      <MagicOverlay show={uploadMutation.isPending || deleteMutation.isPending} />

      {/* Page header */}
      <Box sx={{ mx: 2, mt: 3, mb: 1.5, flexShrink: 0 }}>
        <Typography variant="h5" sx={{ px: 2, mb: 1.5, color: "text.primary", fontWeight: 700 }}>
          FORMS
        </Typography>
        <Divider sx={{ borderColor: "divider", my: 1 }} />
      </Box>

      {/* Scrollable body */}
      <Box sx={{ flex: 1, overflowY: "auto", px: 4, pb: 4, display: "flex", flexDirection: "column", gap: 3 }}>

        {/* Upload card */}
        <Box
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          sx={{
            borderRadius: "8px",
            bgcolor: isDragging ? "rgba(23,120,199,0.08)" : "background.paper",
            border: "2px dashed",
            borderColor: isDragging ? "primary.main" : "divider",
            px: 2, py: 2,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            minHeight: 200,
            flexShrink: 0,
            gap: 1.5,
            transition: "border-color 0.15s, background-color 0.15s",
          }}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.doc,.docx"
            multiple
            hidden
            onChange={handleFileChange}
          />

          <UploadFileIcon sx={{ fontSize: "2.5rem", color: isDragging ? "primary.main" : "text.secondary" }} />

          <Typography sx={{ color: "text.primary", fontWeight: 600, fontSize: "0.95rem" }}>
            {isDragging ? "Drop your files here" : "Drag & drop PDF files here"}
          </Typography>
          <Typography sx={{ color: "text.secondary", fontSize: "0.8rem" }}>
            or
          </Typography>

          <Box
            onClick={handleUploadClick}
            sx={{
              width: "100%", maxWidth: 680,
              bgcolor: "#29ABE2", borderRadius: "6px", py: 2.2,
              display: "flex", alignItems: "center", justifyContent: "center", gap: 1.5,
              cursor: uploadMutation.isPending ? "not-allowed" : "pointer",
              opacity: uploadMutation.isPending ? 0.75 : 1,
              "&:hover": { filter: uploadMutation.isPending ? "none" : "brightness(1.1)" },
              transition: "filter 0.15s",
            }}
          >
            {uploadMutation.isPending
              ? <CircularProgress size={20} sx={{ color: "common.white" }} />
              : <UploadFileIcon sx={{ color: "common.white", fontSize: "1.3rem" }} />
            }
            <Typography sx={{ color: "common.white", fontWeight: 700, fontSize: "0.9rem", letterSpacing: "0.08em" }}>
              {uploadMutation.isPending ? "UPLOADING..." : "UPLOAD FORM"}
            </Typography>
          </Box>

          <Typography sx={{ color: "text.disabled", fontSize: "0.75rem" }}>
            Supported: PDF, DOC, DOCX &nbsp;·&nbsp; Max 25 MB per file
          </Typography>
        </Box>

        {/* Uploaded forms list */}
        {forms.length > 0 && (
          <Box
            sx={{
              borderRadius: "8px",
              bgcolor: "background.paper",
              border: "2px dashed",
              borderColor: "divider",
              px: 6, py: 4,
            }}
          >
            <Typography variant="body1" sx={{ fontWeight: 700, color: "text.primary", mb: 2, letterSpacing: "0.05em" }}>
              UPLOADED FORMS
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, px: 1.5, py: 0.75, mb: 2, border: "1px solid", borderColor: "divider", borderRadius: "8px", bgcolor: "background.default" }}>
              <SearchIcon sx={{ color: "text.secondary", fontSize: "1.1rem" }} />
              <InputBase
                placeholder="Search forms..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                sx={{ flex: 1, fontSize: "0.875rem", color: "text.primary" }}
              />
            </Box>
            <Box
              sx={{
                bgcolor: "background.default",
                borderRadius: "6px",
                border: "1px solid",
                borderColor: "divider",
                overflow: "hidden",
              }}
            >
              {filteredForms.length > 0 ? filteredForms.map((form, idx) => (
                <Box
                  key={form.form_id}
                  sx={{
                    display: "flex", alignItems: "center", justifyContent: "space-between",
                    px: 2.5, py: 1.75,
                    borderBottom: idx < filteredForms.length - 1 ? "1px solid" : "none",
                    borderColor: "divider",
                    "&:hover": { bgcolor: "action.hover" },
                    transition: "background-color 0.12s",
                  }}
                >
                  <Typography variant="body2" sx={{ color: "text.primary", fontWeight: 500 }}>
                    {form.s3_path.split('/').pop()}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={() => setConfirmId(form.form_id)}
                    sx={{
                      bgcolor: "#EF4444", borderRadius: "4px", width: 28, height: 28,
                      "&:hover": { bgcolor: "#c62828" },
                      transition: "background-color 0.15s",
                    }}
                  >
                    <DeleteIcon sx={{ color: "common.white", fontSize: "0.95rem" }} />
                  </IconButton>
                </Box>
              )) : (
                <Typography variant="body2" sx={{ color: "text.secondary", textAlign: "center", py: 3 }}>
                  No matching forms.
                </Typography>
              )}
            </Box>
          </Box>
        )}

      </Box>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={confirmId !== null}
        onClose={() => setConfirmId(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: { bgcolor: "background.paper", border: "1px solid", borderColor: "divider", borderRadius: "10px" },
        }}
      >
        <DialogContent sx={{ p: 0, display: "flex", flexDirection: "column" }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 3, pt: 2.5, pb: 2, borderBottom: "1px solid", borderColor: "divider" }}>
            <Typography sx={{ fontWeight: 700, fontSize: "1rem", color: "text.primary" }}>
              Delete Form
            </Typography>
            <IconButton size="small" onClick={() => setConfirmId(null)} sx={{ color: "text.secondary", "&:hover": { color: "text.primary" } }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
          <Box sx={{ px: 3, py: 3 }}>
            <Typography sx={{ color: "text.primary", fontSize: "0.9rem" }}>
              Are you sure you want to delete this form? This action cannot be undone.
            </Typography>
            {connectedCount > 0 && (
              <Typography sx={{ color: "warning.main", fontSize: "0.82rem", mt: 1.5 }}>
                Note: This form is connected to {connectedCount} file{connectedCount !== 1 ? "s" : ""} in the library. Deleting it will disconnect them.
              </Typography>
            )}
            {submissionCount > 0 && (
              <Typography sx={{ color: "warning.main", fontSize: "0.82rem", mt: 1.5 }}>
                Note: This form has {submissionCount} submission{submissionCount !== 1 ? "s" : ""}. Deleting it will permanently delete the submission{submissionCount !== 1 ? "s" : ""} belonging to this form.
              </Typography>
            )}
          </Box>
          <Box sx={{ px: 3, pb: 2.5, display: "flex", justifyContent: "flex-end", gap: 1.5 }}>
            <Button variant="outlined" onClick={() => setConfirmId(null)} sx={{ fontWeight: 600, borderRadius: "8px", fontSize: "0.8rem" }}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={handleConfirmDelete}
              disabled={deleteMutation.isPending}
              sx={{ fontWeight: 700, borderRadius: "8px", fontSize: "0.8rem" }}
            >
              {deleteMutation.isPending ? "Deleting..." : "Delete"}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default FormsView;
